import { z } from "zod"
import { emailSchema } from "../common/email-validator"
import { CustomerClub, PriceList } from "../store"
import {
  DamperBookingInfo,
  DamperServiceActionName,
  DamperServicePrice,
} from "./damper-service"
import { LegacyCompatibleDiscountSchema } from "./discount"
import {
  Mechanic,
  ServiceBike,
  ServiceBikePartWithStoreSettings,
  ServicePartWithAllInfo,
  WithOptionalGrade,
} from "./index"
import { DiscountCodeOnJob } from "./job"
import { JobCustomer } from "./job-customer"
import {
  ManualServiceAction,
  ManualServiceActionSchema,
} from "./manual-service-action"
import { ServiceAvailability } from "./service-availability"
import { BookingType } from "./service-booking/service-booking"
import { ServiceFee } from "./service-fee"
import { ServicePackage } from "./service-package"
import {
  ServiceWash,
  ServiceWashSnapshot,
  ServiceWashSnapshotSchema,
} from "./service-wash"

export interface DataRequiredForReception {
  mechanics: Mechanic[]
  serviceParts: ServiceBikePartWithStoreSettings[]
  customerClub: CustomerClub | null
  customerInfoFromBooking: CustomerInfoFromBooking | null
  availableServicePackages: ServicePackage[]
  availableFees: ServiceFee[]
  availableWashes: ServiceWash[]
  bike: ServiceBike | null
  assignedMechanicId: string | null
  /** Logged-in user's linked Personal, else the booking's assigned mechanic, else the store's last receiver */
  defaultReceivingMechanicId: string | null
  discountCode: DiscountCodeOnJob | null
  damperServicePrices: DamperServicePrice[]
  damperBrands: { id: string; name: string }[]
  damperActionNames: DamperServiceActionName[]
  occupiedBikeTags: number[]
  physicalTag: number | null
  /** The job's saved Leasing-ID, so reception doesn't clear it. */
  leasingId: string | null
  sameDayBookingCutoffTime: string | null
  serviceAvailability: ServiceAvailability
  handInDate: Date | null
  priceLists: PriceList[]
}

export interface CustomerInfoFromBooking {
  customer: JobCustomer
  selectedWashId: string | null
  selectedPackageIds: string[]
  customerInfoCanBeStored: boolean
  customerWantsToJoinStoreClub: boolean
  customerBudget: number | null
  checkInComment: string | null
  damperBookingInfos: DamperBookingInfo[]
  /** Custom-section items the customer booked, prefilled as manual actions. */
  bookedCustomItems: BookedCustomItem[]
  bookingType: BookingType | null
  isEmergency: boolean
}

export const ServiceWashInReceptionSchema = ServiceWashSnapshotSchema.pick({
  originalWashId: true,
  isMandatory: true,
}).extend({
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable().default(null),
})

/**
 * A custom-section item the customer booked, as the manual action the
 * reception prefills for it. `itemId` points at the store's section item, so
 * the reception can recognise the action however the mechanic edits it.
 */
export interface BookedCustomItem {
  itemId: string
  manualServiceAction: ManualServiceActionInReception
}

export interface CustomBookingSectionItemSnapshot {
  id: string
  /** The store's section item this snapshot was taken from. */
  itemId: string
  name: string
  description: string | null
  price: number
}

/** A service package the customer agreed to at drop-off. */
export interface ReceptionPackage {
  name: string
  price: number
}

export interface Reception {
  id: string
  bike: ServiceBike
  customerInfoCanBeStored: boolean
  customerBudget: number
  evaluatedParts: WithOptionalGrade<ServicePartWithAllInfo>[]
  manualServiceActions: ManualServiceAction[]
  packages: ReceptionPackage[]
  wash: ServiceWashSnapshot | null
  fees: ServiceFee[]
  customItems: CustomBookingSectionItemSnapshot[]

  mechanic: Mechanic
  comment: string | null
  createdAt: Date
}

export const CustomerInfoInReceptionSchema = z.object({
  firstName: z.string(),
  lastName: z.string().optional(),
  phoneNumber: z.string(),
  email: emailSchema().optional(),
})

export const ManualServiceActionInReceptionSchema =
  ManualServiceActionSchema.omit({
    requiredManualServiceActionIds: true,
    requiredWashId: true,
    requiredPartIds: true,
  })

export type ManualServiceActionInReception = z.infer<
  typeof ManualServiceActionInReceptionSchema
>
